import { useEffect, useState } from "react";
import { format } from "date-fns";
import { Plus, Trash2, Loader2, ShieldCheck, Cake, CalendarIcon, KeyRound, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/Avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtBday = (dob) => (dob && dob.length >= 10 ? `${MONTHS[parseInt(dob.slice(5, 7), 10) - 1]} ${parseInt(dob.slice(8, 10), 10)}` : "");

export default function Team() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "consultant", country: "India", date_of_birth: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error("Name and email required"); return; }
    setSaving(true);
    try {
      await api.post("/users", form);
      toast.success("User added · password TSG@2026");
      setOpen(false);
      setForm({ name: "", email: "", role: "consultant", country: "India", date_of_birth: "" });
      load();
    } catch (e) {
      toast.error(apiError(e, "Could not add user"));
    } finally {
      setSaving(false);
    }
  };

  const setBirthday = async (id, dob) => {
    await api.patch(`/users/${id}`, { date_of_birth: dob });
    toast.success("Birthday updated");
    load();
  };

  const [resetUser, setResetUser] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [requireChange, setRequireChange] = useState(true);
  const [resetting, setResetting] = useState(false);

  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", country: "", date_of_birth: "" });
  const [savingEdit, setSavingEdit] = useState(false);

  const openEdit = (u) => { setEditUser(u); setEditForm({ name: u.name || "", country: u.country || "", date_of_birth: u.date_of_birth || "" }); };
  const saveEdit = async () => {
    if (!editForm.name.trim()) { toast.error("Name is required"); return; }
    setSavingEdit(true);
    try {
      await api.patch(`/users/${editUser.id}`, editForm);
      toast.success("Profile updated");
      setEditUser(null);
      load();
    } catch (e) {
      toast.error(apiError(e, "Could not update profile"));
    } finally {
      setSavingEdit(false);
    }
  };

  const openReset = (u) => { setResetUser(u); setNewPw(""); setRequireChange(true); };
  const doReset = async () => {
    if (newPw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setResetting(true);
    try {
      await api.post(`/users/${resetUser.id}/reset-password`, { new_password: newPw, require_change: requireChange });
      toast.success(`Password reset for ${resetUser.name}`);
      setResetUser(null);
    } catch (e) {
      toast.error(apiError(e, "Could not reset password"));
    } finally {
      setResetting(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/users/${id}`);
    toast.success("User removed");
    load();
  };

  const admins = users.filter((u) => u.role === "admin");
  const consultants = users.filter((u) => u.role === "consultant");

  const Card = ({ u }) => (
    <div data-testid={`team-user-${u.id}`} className="flex items-center gap-3 bg-white border border-border rounded-lg p-4">
      <Avatar name={u.name} size={40} />
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-sm flex items-center gap-1.5">
          {u.name}
          {u.role === "admin" && <ShieldCheck className="h-3.5 w-3.5 text-blue-500" />}
        </div>
        <div className="text-xs text-muted-foreground truncate">{u.email}</div>
      </div>
      {u.country && <span className="text-xs text-muted-foreground">{u.country}</span>}
      <Popover>
        <PopoverTrigger asChild>
          <button
            data-testid={`edit-birthday-${u.id}`}
            className="text-xs inline-flex items-center gap-1.5 border border-border rounded-full px-2.5 py-1 hover:bg-secondary transition-colors"
            title="Set birthday"
          >
            <Cake className="h-3.5 w-3.5 text-pink-500" />
            {u.date_of_birth ? fmtBday(u.date_of_birth) : "Set"}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <Calendar
            mode="single"
            captionLayout="dropdown-buttons"
            fromYear={1940}
            toYear={2030}
            selected={u.date_of_birth ? new Date(u.date_of_birth) : undefined}
            onSelect={(d) => d && setBirthday(u.id, format(d, "yyyy-MM-dd"))}
          />
        </PopoverContent>
      </Popover>
      <button
        data-testid={`edit-profile-${u.id}`}
        onClick={() => openEdit(u)}
        className="text-muted-foreground hover:text-foreground p-1.5 transition-colors"
        title="Edit profile"
      >
        <Pencil className="h-4 w-4" />
      </button>
      <button
        data-testid={`reset-password-${u.id}`}
        onClick={() => openReset(u)}
        className="text-muted-foreground hover:text-foreground p-1.5 transition-colors"
        title="Reset password"
      >
        <KeyRound className="h-4 w-4" />
      </button>
      {u.id !== user?.id && (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button data-testid={`delete-user-${u.id}`} className="text-muted-foreground hover:text-destructive p-1.5 transition-colors"><Trash2 className="h-4 w-4" /></button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove {u.name}?</AlertDialogTitle>
              <AlertDialogDescription>They will lose access immediately. Their customers remain in the system.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => remove(u.id)} className="bg-destructive hover:bg-destructive/90">Remove</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );

  return (
    <div className="p-8 max-w-[1000px] animate-in-up">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Team</h1>
          <p className="text-muted-foreground mt-1">{users.length} members</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-user-button" className="gap-2 font-semibold"><Plus className="h-4 w-4" /> Add User</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-display tracking-tight">Add Team Member</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1.5"><Label>Name</Label><Input data-testid="new-user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Email</Label><Input data-testid="new-user-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Role</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="consultant">Product Consultant</SelectItem>
                      <SelectItem value="admin">Global Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Country</Label>
                  <Select value={form.country} onValueChange={(v) => setForm({ ...form, country: v })}>
                    <SelectTrigger data-testid="new-user-country"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="India">India</SelectItem>
                      <SelectItem value="China">China</SelectItem>
                      <SelectItem value="Global">Global</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Date of Birth (optional)</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button data-testid="new-user-dob" variant="outline" className="w-full justify-start font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {form.date_of_birth || <span className="text-muted-foreground">Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar mode="single" captionLayout="dropdown-buttons" fromYear={1940} toYear={2030}
                      selected={form.date_of_birth ? new Date(form.date_of_birth) : undefined}
                      onSelect={(d) => setForm({ ...form, date_of_birth: d ? format(d, "yyyy-MM-dd") : "" })} />
                  </PopoverContent>
                </Popover>
              </div>
              <p className="text-xs text-muted-foreground">Default password will be <code className="bg-secondary px-1 rounded">TSG@2026</code></p>
            </div>
            <DialogFooter>
              <Button data-testid="save-user-button" onClick={create} disabled={saving} className="gap-2 font-semibold">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add User"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mb-8">
        <h2 className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Global Admins</h2>
        <div className="grid sm:grid-cols-2 gap-3">{admins.map((u) => <Card key={u.id} u={u} />)}</div>
      </div>
      <div>
        <h2 className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Product Consultants</h2>
        <div className="grid sm:grid-cols-2 gap-3">{consultants.map((u) => <Card key={u.id} u={u} />)}</div>
      </div>

      <Dialog open={!!editUser} onOpenChange={(o) => { if (!o) setEditUser(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight flex items-center gap-2"><Pencil className="h-5 w-5" /> Edit Profile</DialogTitle>
            <DialogDescription>Maintain this team member's profile. One person = one profile.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Name</Label><Input data-testid="edit-profile-name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Country</Label><Input data-testid="edit-profile-country" value={editForm.country} onChange={(e) => setEditForm({ ...editForm, country: e.target.value })} placeholder="India / China / Global" /></div>
            <div className="space-y-1.5">
              <Label>Date of Birth</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button data-testid="edit-profile-dob" variant="outline" className="w-full justify-start font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {editForm.date_of_birth || <span className="text-muted-foreground">Pick a date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar mode="single" captionLayout="dropdown-buttons" fromYear={1940} toYear={2030}
                    selected={editForm.date_of_birth ? new Date(editForm.date_of_birth) : undefined}
                    onSelect={(d) => setEditForm({ ...editForm, date_of_birth: d ? format(d, "yyyy-MM-dd") : "" })} />
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="edit-profile-submit" onClick={saveEdit} disabled={savingEdit} className="gap-2 font-semibold">
              {savingEdit ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Profile"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetUser} onOpenChange={(o) => { if (!o) setResetUser(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight flex items-center gap-2"><KeyRound className="h-5 w-5" /> Reset Password</DialogTitle>
            <DialogDescription>Set a new password for this user. You don't need their current one.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Set a new password for <span className="font-semibold text-foreground">{resetUser?.name}</span>. You do not need their current password.</p>
            <div className="space-y-1.5">
              <Label>New Password</Label>
              <Input data-testid="reset-new-password" type="text" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="At least 6 characters" />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input data-testid="reset-require-change" type="checkbox" checked={requireChange} onChange={(e) => setRequireChange(e.target.checked)} className="h-4 w-4 rounded border-border" />
              Require this user to set a new password at next login
            </label>
          </div>
          <DialogFooter>
            <Button data-testid="reset-password-submit" onClick={doReset} disabled={resetting} className="gap-2 font-semibold">
              {resetting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reset Password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
